import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import {
  ArrowLeft, Phone, PhoneIncoming, PhoneOff, PhoneMissed, MessageSquare,
  Clock, Users, Activity, Radio, BarChart3, TrendingUp, Headphones,
  PhoneOutgoing, Timer, Award, Play, Pause, Volume2, Mic, ChevronDown, ChevronUp
} from "lucide-react";
import { useNavigate } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
axios.defaults.withCredentials = true;

const fmtDuration = (s) => {
  if (!s) return "0s";
  s = Math.round(s);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${sec}s`;
  return `${sec}s`;
};

const fmtTime = (iso) => {
  if (!iso) return "-";
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  } catch { return "-"; }
};

const StatCard = ({ label, value, sub, icon: Icon, color, bg }) => (
  <div className={`${bg} rounded-xl p-4 relative overflow-hidden`} data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
    <div className="flex items-start justify-between">
      <div>
        <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">{label}</p>
        <p className={`text-3xl font-bold ${color} mt-1 leading-none`}>{value}</p>
        {sub && <p className="text-[10px] text-gray-400 mt-1">{sub}</p>}
      </div>
      <div className={`p-2 rounded-lg ${bg} opacity-60`}>
        <Icon className={`h-5 w-5 ${color}`} />
      </div>
    </div>
  </div>
);

const ProgressBar = ({ value, max, color }) => {
  const pct = max > 0 ? Math.min((value / max) * 100, 100) : 0;
  return (
    <div className="h-2 bg-gray-100 rounded-full overflow-hidden w-full">
      <div className={`h-full rounded-full transition-all duration-500 ${color}`} style={{ width: `${pct}%` }} />
    </div>
  );
};

const RecordingPlayer = ({ recordingId }) => {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);

  const togglePlay = async () => {
    if (!audioUrl && !loading) {
      setLoading(true);
      setError(null);
      try {
        const resp = await axios.get(`${API}/ringcentral/recording/${recordingId}`, { responseType: "blob" });
        const url = URL.createObjectURL(resp.data);
        setAudioUrl(url);
        setTimeout(() => {
          if (audioRef.current) { audioRef.current.play(); setPlaying(true); }
        }, 100);
      } catch (e) {
        const errMsg = e.response?.status === 502 ? "Permission needed: ReadCallRecording" : "Failed to load";
        setError(errMsg);
      }
      setLoading(false);
      return;
    }
    if (audioRef.current) {
      if (playing) { audioRef.current.pause(); }
      else { audioRef.current.play(); }
      setPlaying(!playing);
    }
  };

  return (
    <div className="flex items-center gap-1.5">
      <button onClick={togglePlay} disabled={loading}
        className={`flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-medium transition-all ${
          error ? 'bg-red-50 text-red-400 cursor-default' :
          playing ? 'bg-[#4A7A56]/10 text-[#4A7A56]' :
          'bg-[#2F5D8A]/8 text-[#2F5D8A] hover:bg-[#2F5D8A]/15'
        }`}
        data-testid={`play-recording-${recordingId}`}
        title={error || "Play recording"}>
        {loading ? <div className="animate-spin h-3 w-3 border-b border-[#2F5D8A] rounded-full" /> :
         error ? <Volume2 className="h-3 w-3" /> :
         playing ? <Pause className="h-3 w-3" /> : <Play className="h-3 w-3" />}
        {error ? "No access" : playing ? "Pause" : "Play"}
      </button>
      {audioUrl && (
        <audio ref={audioRef} src={audioUrl} onEnded={() => setPlaying(false)} onPause={() => setPlaying(false)} onPlay={() => setPlaying(true)} />
      )}
    </div>
  );
};

const ProductivityPage = ({ Layout }) => {
  const navigate = useNavigate();
  const [activeView, setActiveView] = useState("phone");
  const [schedData, setSchedData] = useState({ date: "", operators: [], totals: { calls_made: 0, calls_received: 0, sms: 0, emails: 0, appointments_set: 0, total_actions: 0 } });
  const [rcData, setRcData] = useState(null);
  const [rcStatus, setRcStatus] = useState(null);
  const [recentCalls, setRecentCalls] = useState([]);
  const [showRecentCalls, setShowRecentCalls] = useState(true);
  const [loading, setLoading] = useState(true);
  const [dateFilter, setDateFilter] = useState("today");
  const [customDate, setCustomDate] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  });

  const getDateRange = useCallback((filter) => {
    const now = new Date();
    let start, end;
    if (filter === "today") {
      start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      end = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
    } else if (filter === "yesterday") {
      start = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);
      end = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    } else if (filter === "week") {
      const day = now.getDay();
      const diff = day === 0 ? 6 : day - 1;
      start = new Date(now.getFullYear(), now.getMonth(), now.getDate() - diff);
      end = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
    } else {
      const d = new Date(customDate + "T00:00:00");
      start = d;
      end = new Date(d.getTime() + 86400000);
    }
    return {
      from: start.toISOString(),
      to: end.toISOString(),
      dateStr: `${start.getFullYear()}-${String(start.getMonth() + 1).padStart(2, "0")}-${String(start.getDate()).padStart(2, "0")}`,
    };
  }, [customDate]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    const { from, to, dateStr } = getDateRange(dateFilter);
    try {
      const [statusRes, prodRes, schedRes] = await Promise.allSettled([
        axios.get(`${API}/ringcentral/status`),
        axios.get(`${API}/ringcentral/call-productivity`, { params: { date_from: from, date_to: to } }),
        axios.get(`${API}/scheduling/productivity`, { params: { date: dateStr } }),
      ]);
      if (statusRes.status === "fulfilled") setRcStatus(statusRes.value.data);
      if (prodRes.status === "fulfilled") {
        setRcData(prodRes.value.data);
        setRecentCalls(prodRes.value.data.recent_calls || []);
      }
      if (schedRes.status === "fulfilled") setSchedData(schedRes.value.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [dateFilter, getDateRange]);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    if (dateFilter !== "today") return;
    const interval = setInterval(fetchData, 60000);
    return () => clearInterval(interval);
  }, [dateFilter, fetchData]);

  const formatDateLabel = () => {
    const { from } = getDateRange(dateFilter);
    if (dateFilter === "week") return "This Week (Mon-Today)";
    try {
      const d = new Date(from);
      const days = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
      const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
      return `${days[d.getDay()]}, ${months[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
    } catch { return ""; }
  };

  const totals = rcData?.totals || { total_calls: 0, total_duration: 0, answered: 0, missed: 0, sms_sent: 0 };
  const operators = rcData?.operators || [];
  const maxCalls = Math.max(...operators.map(o => o.total_calls), 1);

  return (
    <Layout>
      <div className="space-y-5 max-w-6xl mx-auto" data-testid="productivity-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate("/scheduling")} className="p-1.5 rounded-lg bg-gray-200 hover:bg-gray-300 text-gray-700" data-testid="back-to-scheduling">
              <ArrowLeft className="h-4 w-4" />
            </button>
            <div>
              <h1 className="text-xl font-bold text-[#1F2937] flex items-center gap-2">
                <BarChart3 className="h-5 w-5 text-[#2F5D8A]" />
                Operator Productivity
              </h1>
              <p className="text-gray-400 text-sm">Performance tracking per operator</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {dateFilter === "today" && (
              <span className="flex items-center gap-1 text-[10px] text-gray-400 mr-2">
                <Radio className="h-3 w-3 text-[#6B8F62] animate-pulse" /> Live
              </span>
            )}
            {rcStatus?.connected && (
              <span className="flex items-center gap-1 px-2 py-1 rounded-full text-[9px] font-bold bg-[#6B8F62]/10 text-[#6B8F62] border border-[#6B8F62]/20" data-testid="rc-connected-badge">
                <Phone className="h-2.5 w-2.5" /> RingCentral
              </span>
            )}
          </div>
        </div>

        {/* View Tabs */}
        <div className="flex items-center gap-2">
          <div className="flex gap-1 bg-white p-1 rounded-lg border border-gray-200">
            <button onClick={() => setActiveView("phone")} className={`px-4 py-2 rounded-md text-xs font-medium flex items-center gap-1.5 transition-all ${activeView === "phone" ? "bg-[#2F5D8A] text-white" : "text-gray-500 hover:text-[#1F2937] hover:bg-gray-50"}`} data-testid="view-phone">
              <Headphones className="h-3.5 w-3.5" /> Phone Activity
            </button>
            <button onClick={() => setActiveView("scheduling")} className={`px-4 py-2 rounded-md text-xs font-medium flex items-center gap-1.5 transition-all ${activeView === "scheduling" ? "bg-[#2F5D8A] text-white" : "text-gray-500 hover:text-[#1F2937] hover:bg-gray-50"}`} data-testid="view-scheduling">
              <Activity className="h-3.5 w-3.5" /> Scheduling Activity
            </button>
          </div>
        </div>

        {/* Date Filter */}
        <div className="flex flex-wrap items-center gap-2" data-testid="productivity-date-filter">
          {[
            { key: "today", label: "Today" },
            { key: "yesterday", label: "Yesterday" },
            { key: "week", label: "This Week" },
            { key: "custom", label: "Choose Date" },
          ].map(({ key, label }) => (
            <button key={key} onClick={() => setDateFilter(key)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${dateFilter === key ? "bg-[#2F5D8A] text-white shadow-sm" : "bg-white text-gray-600 border border-gray-200 hover:border-[#2F5D8A]/40 hover:text-[#2F5D8A]"}`}
              data-testid={`prod-date-${key}`}
            >{label}</button>
          ))}
          {dateFilter === "custom" && (
            <input type="date" value={customDate} onChange={e => setCustomDate(e.target.value)}
              className="px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm text-[#1F2937] focus:outline-none focus:border-[#2F5D8A]"
              data-testid="prod-custom-date" />
          )}
          <span className="text-sm text-gray-400 font-medium ml-2">{formatDateLabel()}</span>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#2F5D8A]" />
          </div>
        ) : activeView === "phone" ? (
          /* ═══ PHONE ACTIVITY DASHBOARD ═══ */
          <>
            {!rcStatus?.connected ? (
              <div className="bg-white rounded-xl border border-gray-200 p-8 text-center" data-testid="rc-not-connected">
                <PhoneOff className="h-10 w-10 mx-auto mb-3 text-gray-300" />
                <p className="text-sm font-medium text-gray-500">RingCentral not connected</p>
                <p className="text-xs text-gray-400 mt-1">Add credentials to backend/.env to enable phone tracking</p>
              </div>
            ) : (
              <>
                {/* Summary Cards */}
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="phone-totals">
                  <StatCard label="Total Calls" value={totals.total_calls} sub={fmtDuration(totals.total_duration) + " total"} icon={Phone} color="text-[#1F2937]" bg="bg-gray-50" />
                  <StatCard label="Answered" value={totals.answered} sub={totals.total_calls > 0 ? `${Math.round((totals.answered / totals.total_calls) * 100)}% rate` : "0%"} icon={PhoneIncoming} color="text-[#4A7A56]" bg="bg-[#4A7A56]/5" />
                  <StatCard label="Missed" value={totals.missed} sub={totals.total_calls > 0 ? `${Math.round((totals.missed / totals.total_calls) * 100)}%` : "0%"} icon={PhoneMissed} color="text-[#C07070]" bg="bg-[#C07070]/5" />
                  <StatCard label="Avg Duration" value={fmtDuration(totals.total_calls > 0 ? Math.round(totals.total_duration / totals.total_calls) : 0)} sub="per call" icon={Timer} color="text-[#2F5D8A]" bg="bg-[#2F5D8A]/5" />
                  <StatCard label="SMS Sent" value={totals.sms_sent} icon={MessageSquare} color="text-[#D1A054]" bg="bg-[#D1A054]/5" />
                  <StatCard label="Recordings" value={totals.recordings || 0} icon={Mic} color="text-[#7A6B8F]" bg="bg-[#7A6B8F]/5" />
                </div>

                {/* Operator Ranking */}
                <div data-testid="operator-ranking">
                  <div className="flex items-center gap-2 mb-3">
                    <Award className="h-4 w-4 text-[#D1A054]" />
                    <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Operator Ranking ({operators.length})
                    </h2>
                  </div>

                  {operators.length === 0 ? (
                    <div className="text-center py-12 bg-white rounded-xl border border-gray-100" data-testid="no-phone-data">
                      <Headphones className="h-10 w-10 mx-auto mb-3 text-gray-200" />
                      <p className="text-sm font-medium text-gray-400">No phone activity for this period</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {operators.map((op, idx) => {
                        const answerRate = op.total_calls > 0 ? Math.round((op.answered / op.total_calls) * 100) : 0;
                        return (
                          <div key={op.operator} className="bg-white rounded-xl border border-gray-100 p-4 hover:border-gray-200 transition-colors" data-testid={`op-card-${idx}`}>
                            <div className="flex items-start justify-between mb-3">
                              <div className="flex items-center gap-3">
                                <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-sm font-bold ${idx === 0 ? 'bg-[#D1A054]/15 text-[#D1A054]' : idx === 1 ? 'bg-[#8B9DAF]/15 text-[#5A7089]' : 'bg-gray-100 text-gray-500'}`}>
                                  #{idx + 1}
                                </div>
                                <div>
                                  <p className="text-sm font-bold text-[#1F2937]">{op.operator}</p>
                                  <p className="text-[10px] text-gray-400">
                                    Last activity: {fmtTime(op.last_activity)}
                                  </p>
                                </div>
                              </div>
                              <div className="text-right">
                                <p className="text-2xl font-bold text-[#1F2937]">{op.total_calls}</p>
                                <p className="text-[10px] text-gray-400">calls</p>
                              </div>
                            </div>

                            {/* Progress bar */}
                            <ProgressBar value={op.total_calls} max={maxCalls} color={idx === 0 ? "bg-[#D1A054]" : "bg-[#2F5D8A]"} />

                            {/* Stats row */}
                            <div className="grid grid-cols-7 gap-2 mt-3">
                              <div className="text-center">
                                <p className="text-xs font-bold text-[#4A7A56]">{op.answered}</p>
                                <p className="text-[9px] text-gray-400">Answered</p>
                              </div>
                              <div className="text-center">
                                <p className="text-xs font-bold text-[#C07070]">{op.missed}</p>
                                <p className="text-[9px] text-gray-400">Missed</p>
                              </div>
                              <div className="text-center">
                                <p className="text-xs font-bold text-[#2F5D8A]">{answerRate}%</p>
                                <p className="text-[9px] text-gray-400">Answer Rate</p>
                              </div>
                              <div className="text-center">
                                <p className="text-xs font-bold text-gray-700">{fmtDuration(op.total_duration)}</p>
                                <p className="text-[9px] text-gray-400">Total Time</p>
                              </div>
                              <div className="text-center">
                                <p className="text-xs font-bold text-gray-700">{fmtDuration(op.avg_duration)}</p>
                                <p className="text-[9px] text-gray-400">Avg/Call</p>
                              </div>
                              <div className="text-center">
                                <div className="flex items-center justify-center gap-1">
                                  <span className="text-[9px] text-[#2F5D8A] font-bold">{op.inbound}in</span>
                                  <span className="text-[8px] text-gray-300">/</span>
                                  <span className="text-[9px] text-[#D1A054] font-bold">{op.outbound}out</span>
                                </div>
                                <p className="text-[9px] text-gray-400">Direction</p>
                              </div>
                              <div className="text-center">
                                <p className="text-xs font-bold text-[#7A6B8F]">{op.recordings || 0}</p>
                                <p className="text-[9px] text-gray-400">Recordings</p>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Recent Calls with Recordings */}
                <div data-testid="recent-calls-section">
                  <button onClick={() => setShowRecentCalls(!showRecentCalls)} className="flex items-center gap-2 mb-3 w-full">
                    <Mic className="h-4 w-4 text-[#2F5D8A]" />
                    <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Recent Calls ({recentCalls.length})
                      {totals.recordings > 0 && <span className="ml-1 text-[#4A7A56]">— {totals.recordings} with recording</span>}
                    </h2>
                    {showRecentCalls ? <ChevronUp className="h-3.5 w-3.5 text-gray-400 ml-auto" /> : <ChevronDown className="h-3.5 w-3.5 text-gray-400 ml-auto" />}
                  </button>

                  {showRecentCalls && recentCalls.length > 0 && (
                    <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
                      <div className="overflow-x-auto">
                        <table className="w-full text-xs" data-testid="recent-calls-table">
                          <thead>
                            <tr className="bg-gray-50/80 border-b border-gray-100">
                              <th className="text-left text-[10px] font-semibold text-gray-400 uppercase tracking-wider px-3 py-2.5">Time</th>
                              <th className="text-left text-[10px] font-semibold text-gray-400 uppercase tracking-wider px-3 py-2.5">Direction</th>
                              <th className="text-left text-[10px] font-semibold text-gray-400 uppercase tracking-wider px-3 py-2.5">From</th>
                              <th className="text-left text-[10px] font-semibold text-gray-400 uppercase tracking-wider px-3 py-2.5">To</th>
                              <th className="text-center text-[10px] font-semibold text-gray-400 uppercase tracking-wider px-3 py-2.5">Result</th>
                              <th className="text-center text-[10px] font-semibold text-gray-400 uppercase tracking-wider px-3 py-2.5">Duration</th>
                              <th className="text-left text-[10px] font-semibold text-gray-400 uppercase tracking-wider px-3 py-2.5">Operator</th>
                              <th className="text-center text-[10px] font-semibold text-gray-400 uppercase tracking-wider px-3 py-2.5">Recording</th>
                            </tr>
                          </thead>
                          <tbody>
                            {recentCalls.map((call, idx) => {
                              const resultColor =
                                call.result === "Accepted" || call.result === "Call connected" ? "text-[#4A7A56] bg-[#4A7A56]/8" :
                                call.result === "Missed" || call.result === "No Answer" ? "text-[#C07070] bg-[#C07070]/8" :
                                call.result === "Voicemail" ? "text-[#D1A054] bg-[#D1A054]/8" :
                                "text-gray-500 bg-gray-100";
                              return (
                                <tr key={call.rc_id || idx} className="border-b border-gray-50 hover:bg-gray-50/40 transition-colors" data-testid={`call-row-${idx}`}>
                                  <td className="px-3 py-2 whitespace-nowrap text-gray-600">{fmtTime(call.start_time)}</td>
                                  <td className="px-3 py-2">
                                    <span className={`inline-flex items-center gap-1 text-[10px] font-bold ${call.direction === "Inbound" ? "text-[#2F5D8A]" : "text-[#D1A054]"}`}>
                                      {call.direction === "Inbound" ? <PhoneIncoming className="h-3 w-3" /> : <PhoneOutgoing className="h-3 w-3" />}
                                      {call.direction}
                                    </span>
                                  </td>
                                  <td className="px-3 py-2 whitespace-nowrap">
                                    <p className="text-gray-700 font-medium">{call.from_name || call.from_number || "-"}</p>
                                    {call.from_name && call.from_number && <p className="text-[9px] text-gray-400">{call.from_number}</p>}
                                  </td>
                                  <td className="px-3 py-2 whitespace-nowrap">
                                    <p className="text-gray-700 font-medium">{call.to_name || call.to_number || "-"}</p>
                                    {call.to_name && call.to_number && <p className="text-[9px] text-gray-400">{call.to_number}</p>}
                                  </td>
                                  <td className="px-3 py-2 text-center">
                                    <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold ${resultColor}`}>{call.result}</span>
                                  </td>
                                  <td className="px-3 py-2 text-center font-medium text-gray-600">{fmtDuration(call.duration)}</td>
                                  <td className="px-3 py-2 whitespace-nowrap text-gray-500 text-[10px]">{call.operator}</td>
                                  <td className="px-3 py-2 text-center">
                                    {call.has_recording && call.recording_id ? (
                                      <RecordingPlayer recordingId={call.recording_id} />
                                    ) : (
                                      <span className="text-[9px] text-gray-300">—</span>
                                    )}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}
          </>
        ) : (
          /* ═══ SCHEDULING ACTIVITY (existing) ═══ */
          <>
            <div data-testid="team-totals">
              <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Scheduling Totals</h2>
              <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
                <StatCard label="Total Actions" value={schedData.totals.total_actions} icon={Activity} color="text-[#1F2937]" bg="bg-gray-50" />
                <StatCard label="Calls Made" value={schedData.totals.calls_made} icon={PhoneOutgoing} color="text-[#2F5D8A]" bg="bg-[#2F5D8A]/8" />
                <StatCard label="Calls Received" value={schedData.totals.calls_received} icon={PhoneIncoming} color="text-[#6B8F62]" bg="bg-[#6B8F62]/8" />
                <StatCard label="SMS Sent" value={schedData.totals.sms} icon={MessageSquare} color="text-[#D1A054]" bg="bg-[#D1A054]/10" />
                <StatCard label="Emails Sent" value={schedData.totals.emails} icon={TrendingUp} color="text-[#7A6B8F]" bg="bg-[#7A6B8F]/8" />
                <StatCard label="Appointments" value={schedData.totals.appointments_set} icon={Clock} color="text-[#4A7A56]" bg="bg-[#4A7A56]/8" />
              </div>
            </div>

            <div data-testid="sched-operator-breakdown">
              <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                By Operator ({schedData.operators.length})
              </h2>
              {schedData.operators.length === 0 ? (
                <div className="text-center py-12 bg-white rounded-xl border border-gray-100">
                  <Users className="h-10 w-10 mx-auto mb-3 text-gray-200" />
                  <p className="text-sm font-medium text-gray-400">No scheduling activity for this date</p>
                </div>
              ) : (
                <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-gray-50/80 border-b border-gray-100">
                        <th className="text-left text-[10px] font-semibold text-gray-400 uppercase tracking-wider px-4 py-3">Operator</th>
                        <th className="text-center text-[10px] font-semibold text-gray-400 uppercase tracking-wider px-3 py-3">Total</th>
                        <th className="text-center text-[10px] font-semibold text-[#2F5D8A] uppercase tracking-wider px-3 py-3">Calls Made</th>
                        <th className="text-center text-[10px] font-semibold text-[#6B8F62] uppercase tracking-wider px-3 py-3">Calls Recv</th>
                        <th className="text-center text-[10px] font-semibold text-[#D1A054] uppercase tracking-wider px-3 py-3">SMS</th>
                        <th className="text-center text-[10px] font-semibold text-[#7A6B8F] uppercase tracking-wider px-3 py-3">Emails</th>
                        <th className="text-center text-[10px] font-semibold text-[#4A7A56] uppercase tracking-wider px-3 py-3">Appts Set</th>
                      </tr>
                    </thead>
                    <tbody>
                      {schedData.operators.map((op, idx) => {
                        const pctOfTotal = schedData.totals.total_actions > 0 ? Math.round((op.total_actions / schedData.totals.total_actions) * 100) : 0;
                        return (
                          <tr key={op.operator_name} className="border-b border-gray-50 hover:bg-gray-50/40 transition-colors">
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-2">
                                <div className="w-7 h-7 rounded-full bg-[#2F5D8A]/10 flex items-center justify-center text-[10px] font-bold text-[#2F5D8A]">{op.operator_name.charAt(0)}</div>
                                <div>
                                  <p className="text-sm font-semibold text-[#1F2937]">{op.operator_name}</p>
                                  <div className="flex items-center gap-1 mt-0.5">
                                    <div className="h-1 w-16 bg-gray-100 rounded-full overflow-hidden"><div className="h-full bg-[#2F5D8A]/40 rounded-full" style={{ width: `${pctOfTotal}%` }} /></div>
                                    <span className="text-[9px] text-gray-400">{pctOfTotal}%</span>
                                  </div>
                                </div>
                              </div>
                            </td>
                            <td className="px-3 py-3 text-center"><span className="text-sm font-bold text-[#1F2937]">{op.total_actions}</span></td>
                            <td className="px-3 py-3 text-center"><span className={`text-sm font-medium ${op.calls_made > 0 ? 'text-[#2F5D8A]' : 'text-gray-300'}`}>{op.calls_made}</span></td>
                            <td className="px-3 py-3 text-center"><span className={`text-sm font-medium ${op.calls_received > 0 ? 'text-[#6B8F62]' : 'text-gray-300'}`}>{op.calls_received}</span></td>
                            <td className="px-3 py-3 text-center"><span className={`text-sm font-medium ${op.sms > 0 ? 'text-[#D1A054]' : 'text-gray-300'}`}>{op.sms}</span></td>
                            <td className="px-3 py-3 text-center"><span className={`text-sm font-medium ${op.emails > 0 ? 'text-[#7A6B8F]' : 'text-gray-300'}`}>{op.emails}</span></td>
                            <td className="px-3 py-3 text-center"><span className={`text-sm font-medium ${op.appointments_set > 0 ? 'text-[#4A7A56]' : 'text-gray-300'}`}>{op.appointments_set}</span></td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </Layout>
  );
};

export default ProductivityPage;
